import { enums, resources } from '../../../gads'
import { BiddingStrategy } from './../../../bidding-strategies/bidding_strategies'
import { Improvement } from '../../improvement'
import { GadsFieldsRequired } from '../../../utils/global'
import { Targets } from '../../..'

export namespace NgramImpType {
    // The core data of the improvement, available in the front-end

    export interface ScatterPlotPoint {
        name: string
        metrics: {
            cost: number
            all_conversions: number
            all_conversions_value: number
            cpa?: number
            roas?: number
            profit?: number
        }
        focus: boolean
    }

    export interface Body {
        ngram: {
            text: string
            frequency: number
            cost: number
            clicks: number
            all_conversions: number
            all_conversions_value: number
            cpa?: number
            roas?: number
            roi?: number
            profit?: number
        }
        campaign_group: {
            name: string
            bidding_strategy: BiddingStrategy
            is_target_set: boolean
            cpa_target_or_avg?: number
            roas_target_or_avg?: number
            campaign_rns: string[]
        }
        ngram_graph_points: ScatterPlotPoint[]
        keywords_to_reduce_bids: {
            resource_name: string
            text_inc_matchtype: string
            current_bid: number
            new_bid: number
            campaign_name: string
            adgroup_name: string
            cost: number
            all_conversions: number
            all_conversions_value: number
        }[]
        search_terms: {
            text: string
        }[]
        advertising_channel_type: enums.AdvertisingChannelType
        window: number
        partial_ngrams: {
            text: string
            cost: number
            all_conversions: number
            all_conversions_value: number
            cpa?: number
            roas?: number
            roi?: number
            profit?: number
            conflicting_keywords: string[]
        }[]
        default_negative_level: 'shared' | 'campaign'
        performance_mode: Targets.PerformanceMode
    }

    // Metadata provided when pushing/adjusting improvement
    export interface NegativeNgramExtraDetails {
        included_negative_levels: Record<'shared' | 'campaign', boolean>
        ngram_text: string
        shared_set_rns: string[]
        includes_account_level: boolean
        campaign_rns: string[]
        campaign_ids?: number[]
        ad_group_ids?: number[]
        match_type?: enums.KeywordMatchType
    }

    // Error codes
    export const Error = {
        // KEYWORD_NO_LONGER_EXISTS: 'KEYWORD_NO_LONGER_EXISTS',
    } as const

    export type Error = (typeof Error)[keyof typeof Error]

    // Human readable error message, used by support
    export const ErrorReadable = {
        // [Error.KEYWORD_NO_LONGER_EXISTS]:
        //     'The keyword we wanted to pause has already been deleted.',
    }

    export enum NoImprovementReason {
        no_search_campaigns_found = 'no_search_campaigns_found',
    }
}

// Data processing types, not explicitly related to core types

export interface PositiveKeyword {
    keyword_text: string
    resource_name: string
    bidding_strategy_type: enums.BiddingStrategyType
    cpc_bid: number
    new_cpc_bid: number
    match_type: enums.KeywordMatchType
    first_page_cpc_estimate?: number
    campaign_rn: string
    campaign_name: string
    adgroup_name: string
    metrics: {
        cost: number
        all_conversions: number
        all_conversions_value: number
        window: Improvement.LookbackWindow
    }[]
}

export interface SearchTerm {
    text: string
    adGroupRn: string
    campaignRn: string
    advertising_channel_type: enums.AdvertisingChannelType
    metrics: {
        cost: number
        clicks: number
        all_conversions: number
        all_conversions_value: number
        window: Improvement.LookbackWindow
    }[]
}

export interface Ngram {
    ngram_text: string
    search_terms: SearchTerm[]
    advertising_channel_type: enums.AdvertisingChannelType
    metrics: {
        window: Improvement.LookbackWindow
        clicks: number
        cost: number
        all_conversions: number
        all_conversions_value: number
        search_term_count: number
        frequency: number
    }[]
    best_window?: number
}

export type SelectableCampaignsForNegativeKeyword = GadsFieldsRequired<
    resources.Campaign,
    'resource_name' | 'name' | 'advertising_channel_sub_type' | 'advertising_channel_type'
>[]

export type SelectableCampaignsForNegativeKeywordMultiPlatform = {
    name: string
    campaign_id: string
}[]

export interface DismissedNgram {
    ngram_text: string
    rec_action: Improvement.RecAction
}
